import { useEffect, useState } from "react";
import { Users, DollarSign, TicketPercent, TrendingUp, Download, Loader2, Handshake, Shirt, FileText, Tag, Gift } from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, absUrl } from "@/lib/api";
import { brl, STATUS, SPORT_LABEL } from "@/lib/format";

const STATUS_COLORS = { PAID: "#10B981", PENDING_PAYMENT: "#F59E0B", PRE_REGISTERED: "#3B82F6", CANCELLED: "#EF4444" };

export default function Dashboard() {
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/registrations/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "inscricoes-run-cemoc.csv"; a.click();
      toast.success("CSV exportado!");
    } catch { toast.error("Falha ao exportar"); }
  };

  const exportPdf = async () => {
    try {
      const res = await api.get("/admin/financial-report", { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Falha ao gerar relatório"); }
  };

  const exportParticipants = async (sport) => {
    try {
      const res = await api.get("/admin/participants-report", { params: sport ? { sport_type: sport } : {}, responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Falha ao gerar relatório de inscritos"); }
  };

  if (!d) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;

  const statusData = Object.entries(d.by_status).map(([k, v]) => ({ name: STATUS[k]?.label || k, value: v, key: k }));
  const cards = [
    { t: "Total de Inscritos", v: d.total, sub: `${d.active_regs} ativos`, i: Users, c: "#2FA34B" },
    { t: "Receita Arrecadada", v: brl(d.revenue_collected), sub: `Estimada ${brl(d.revenue_estimated)}`, i: DollarSign, c: "#10B981" },
    { t: "Vagas Restantes", v: d.remaining_total, sub: `de ${d.total_slots} vagas`, i: TicketPercent, c: "#A3E635" },
    { t: "Taxa de Aprovação PIX", v: `${d.approval_rate}%`, sub: `${d.by_status.PAID} pagos`, i: TrendingUp, c: "#F59E0B" },
  ];

  return (
    <div className="space-y-8 animate-fade-up" data-testid="admin-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-brand font-bold uppercase tracking-widest text-xs">Visão Geral</p>
          <h1 className="font-display text-4xl font-black uppercase">Dashboard</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="participants-report-btn" className="bg-white/5 border border-white/10 hover:bg-white/10 font-bold"><Users className="w-4 h-4 mr-2" /> Inscritos PDF</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-surface border-white/10 text-white">
              <DropdownMenuItem data-testid="participants-report-all" onClick={() => exportParticipants()}>Todas as modalidades</DropdownMenuItem>
              {["CORRIDA", "CICLISMO", "KIDS"].map((s) => <DropdownMenuItem key={s} data-testid={`participants-report-${s}`} onClick={() => exportParticipants(s)}>{SPORT_LABEL[s]}</DropdownMenuItem>)}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button data-testid="financial-report-btn" onClick={exportPdf} className="bg-brand hover:bg-brand-hover font-bold">
            <FileText className="w-4 h-4 mr-2" /> Fechamento PDF
          </Button>
          <Button data-testid="export-csv-btn" onClick={exportCsv} className="bg-white/5 border border-white/10 hover:bg-white/10 font-bold">
            <Download className="w-4 h-4 mr-2" /> Exportar CSV
          </Button>
        </div>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {cards.map((c) => (
          <div key={c.t} data-testid={`metric-${c.t}`} className="rounded-2xl bg-surface border border-white/10 p-5 relative overflow-hidden">
            <div className="absolute right-0 top-0 w-20 h-20 rounded-full blur-2xl opacity-20" style={{ background: c.c }} />
            <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: c.c + "22" }}>
              <c.i className="w-5 h-5" style={{ color: c.c }} />
            </div>
            <div className="font-display text-3xl font-black">{c.v}</div>
            <div className="text-sm text-slate-300 mt-1">{c.t}</div>
            <div className="text-xs text-slate-500 mt-0.5">{c.sub}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 rounded-2xl bg-surface border border-white/10 p-5">
          <h3 className="font-display text-xl uppercase font-bold mb-4">Inscrições por Dia</h3>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={d.per_day}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2FA34B" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#2FA34B" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
              <XAxis dataKey="date" tick={{ fill: "#94A3B8", fontSize: 11 }} tickFormatter={(v) => v?.slice(5)} />
              <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0B0F17", border: "1px solid #ffffff20", borderRadius: 8 }} />
              <Area type="monotone" dataKey="count" stroke="#2FA34B" strokeWidth={2} fill="url(#g1)" name="Inscrições" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-2xl bg-surface border border-white/10 p-5">
          <h3 className="font-display text-xl uppercase font-bold mb-4">Status de Pagamento</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>
                {statusData.map((s) => <Cell key={s.key} fill={STATUS_COLORS[s.key]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#0B0F17", border: "1px solid #ffffff20", borderRadius: 8 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {statusData.map((s) => (
              <div key={s.key} className="flex items-center gap-2 text-xs text-slate-300">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLORS[s.key] }} /> {s.name} ({s.value})
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-2xl bg-surface border border-white/10 p-5">
          <h3 className="font-display text-xl uppercase font-bold mb-4">Distribuição por Sexo & Modalidade</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.distribution}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
              <XAxis dataKey="group" tick={{ fill: "#94A3B8", fontSize: 10 }} />
              <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0B0F17", border: "1px solid #ffffff20", borderRadius: 8 }} cursor={{ fill: "#ffffff08" }} />
              <Bar dataKey="count" fill="#A3E635" radius={[4, 4, 0, 0]} name="Atletas" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl bg-surface border border-white/10 p-5">
          <h3 className="font-display text-xl uppercase font-bold mb-4">Vagas por Categoria</h3>
          <div className="space-y-3 max-h-[240px] overflow-y-auto pr-1">
            {d.per_category.map((c) => {
              const pct = Math.round((c.used / (c.slots_total || 1)) * 100);
              return (
                <div key={c.id}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-300">{c.name}</span>
                    <span className="text-slate-400">{c.used}/{c.slots_total}</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full bg-brand" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Pagamentos — acompanhamento */}
      <div className="rounded-2xl bg-surface border border-white/10 p-5" data-testid="payments-panel">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-display text-xl uppercase font-bold flex items-center gap-2"><DollarSign className="w-5 h-5 text-brand" /> Acompanhamento de Pagamentos</h3>
          <span className="text-xs text-slate-400 mr-auto ml-3">Despesas: <b className="text-red-300" data-testid="dash-expenses">{brl(d.expenses_total || 0)}</b> • Saldo: <b className={d.balance >= 0 ? "text-neon" : "text-red-300"} data-testid="dash-balance">{brl(d.balance || 0)}</b> • Manuais: <b>{d.manual_count || 0}</b></span>
          <span className="text-xs text-slate-400">Links Nubank: <b className="text-neon" data-testid="dash-payment-links">{Object.values(d.payment_links || {}).filter(Boolean).length}</b> configurados</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {[["PAID", "Pagos", d.by_status.PAID, brl(d.revenue_collected)], ["PENDING_PAYMENT", "Aguardando validação", d.payments.awaiting_validation, `${d.payments.with_proof} com comprovante`],
            ["PRE_REGISTERED", "Sem pagamento", d.payments.unpaid, brl(d.payments.unpaid_amount)], ["CANCELLED", "Cancelados / Reembolsos", d.by_status.CANCELLED, `${d.payments.refunds_pending} reembolso(s) pendente(s)`]].map(([k, l, v, sub]) => (
            <div key={k} data-testid={`pay-card-${k}`} className="rounded-xl bg-ink border border-white/10 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">{l}</div>
              <div className="font-display text-3xl font-black" style={{ color: STATUS_COLORS[k] }}>{v}</div>
              <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
            </div>
          ))}
        </div>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 border-b border-white/10">
            <th className="pb-2">Peito</th><th className="pb-2">Atleta</th><th className="pb-2">Valor</th><th className="pb-2">Status</th><th className="pb-2">Comprovante</th><th className="pb-2 text-right">Data</th></tr></thead>
          <tbody>
            {d.payments.recent.map((r) => (
              <tr key={r.bib_code} className="border-b border-white/5" data-testid={`pay-row-${r.bib_code}`}>
                <td className="py-2 font-mono text-neon text-xs">{r.bib_code}</td>
                <td className="py-2">{r.name}</td>
                <td className="py-2 font-mono">{brl(r.amount)}</td>
                <td className="py-2"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${STATUS[r.status]?.bg}`}>{STATUS[r.status]?.label}</span></td>
                <td className="py-2 text-xs">{r.has_proof ? <span className="text-emerald-300">enviado</span> : <span className="text-slate-500">—</span>}</td>
                <td className="py-2 text-right text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString("pt-BR")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-2xl bg-surface border border-white/10 p-5" data-testid="discount-usage-report">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <h3 className="font-display text-lg font-bold uppercase flex items-center gap-2"><Tag className="w-4 h-4 text-brand" /> Uso dos Códigos de Desconto</h3>
          <span className="text-xs text-slate-400">Total: <b className="text-neon" data-testid="discount-total-count">{(d.discount_usage || []).reduce((a, x) => a + x.count, 0)}</b> inscrições • <b className="text-amber-300" data-testid="discount-total-saved">{brl((d.discount_usage || []).reduce((a, x) => a + x.saved, 0))}</b> descontados</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {(d.discount_usage || []).map((x) => (
            <div key={x.code} data-testid={`discount-${x.code}`} className="rounded-xl bg-white/5 border border-white/10 p-3">
              <div className="flex items-center justify-between"><span className="font-mono font-bold text-sm uppercase">{x.code}</span><span className="text-[10px] text-slate-400">{x.percent}%</span></div>
              <div className="font-display text-2xl font-black mt-1">{x.count}</div>
              <div className="text-[11px] text-slate-400">{x.paid} pagos • {brl(x.saved)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl bg-surface border border-white/10 p-5" data-testid="courtesy-list">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <h3 className="font-display text-lg font-bold uppercase flex items-center gap-2"><Gift className="w-4 h-4 text-amber-300" /> Inscrições Cortesia</h3>
          <span className="text-xs text-slate-400">Total: <b className="text-amber-300" data-testid="courtesy-count">{(d.courtesies || []).length}</b></span>
        </div>
        {(d.courtesies || []).length === 0 ? <p className="text-sm text-slate-500">Nenhuma cortesia concedida.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 border-b border-white/10">
                <th className="px-3 py-2">Peito</th><th className="px-3 py-2">Nome</th><th className="px-3 py-2">CPF</th><th className="px-3 py-2">Modalidade</th><th className="px-3 py-2">Cupom</th><th className="px-3 py-2">Data</th></tr></thead>
              <tbody>
                {d.courtesies.map((c) => (
                  <tr key={c.id} data-testid={`courtesy-row-${c.bib_code}`} className="border-b border-white/5">
                    <td className="px-3 py-2 font-mono font-bold">{c.bib_code}</td>
                    <td className="px-3 py-2 font-semibold">{c.name}</td>
                    <td className="px-3 py-2 text-xs text-slate-400 font-mono">{c.cpf ? `${c.cpf.slice(0, 3)}.***.***-${c.cpf.slice(-2)}` : "—"}</td>
                    <td className="px-3 py-2 text-xs">{c.category}</td>
                    <td className="px-3 py-2 text-xs font-mono text-amber-300">{c.code}</td>
                    <td className="px-3 py-2 text-xs text-slate-400">{c.created_at ? new Date(c.created_at).toLocaleDateString("pt-BR") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-2xl bg-surface border border-white/10 p-5" data-testid="shirts-report">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-display text-xl uppercase font-bold flex items-center gap-2"><Shirt className="w-5 h-5 text-brand" /> Camisas por Tamanho</h3>
          <span className="text-xs text-slate-400">Encomendar: <b className="text-neon" data-testid="shirts-total-paid">{d.shirts.reduce((a, s) => a + s.paid, 0)}</b> confirmadas • {d.shirts.reduce((a, s) => a + s.pending, 0)} aguardando pagamento</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {d.shirts.map((s) => (
            <div key={s.size} data-testid={`shirt-size-${s.size}`} className="rounded-xl bg-ink border border-white/10 p-4 text-center">
              <div className="font-display text-3xl font-black text-brand">{s.size}</div>
              <div className="font-display text-2xl font-black mt-1">{s.paid}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">pagas</div>
              <div className="text-xs text-amber-300 mt-1">+{s.pending} pendentes</div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl bg-surface border border-white/10 p-5 flex items-center gap-4">
        <div className="w-11 h-11 rounded-xl bg-tier-gold/20 flex items-center justify-center"><Handshake className="w-5 h-5 text-tier-gold" /></div>
        <div>
          <div className="font-display text-2xl font-black">{d.sponsor_count} patrocinadores</div>
          <div className="text-sm text-slate-400">Investimento total: {brl(d.sponsor_total)}</div>
        </div>
      </div>
    </div>
  );
}
